import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ChevronRight, MapPin, Star, Truck, Shield, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface MatchedCarrier {
  companyName: string;
  safetyScore: number;
  originLocation: string;
  destinationLocation: string;
}

const SAMPLE_CARRIERS: MatchedCarrier[] = [
  { companyName: "Swift Logistics", safetyScore: 98, originLocation: "Dallas, TX", destinationLocation: "Atlanta, GA" },
  { companyName: "Blue Hawk Trucking", safetyScore: 96, originLocation: "Chicago, IL", destinationLocation: "Miami, FL" },
  { companyName: "Torres Freight", safetyScore: 94, originLocation: "Phoenix, AZ", destinationLocation: "Houston, TX" },
];

export default function AICarrierMatchCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [invited, setInvited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<MatchedCarrier[]>([]);
  const [laneCount, setLaneCount] = useState(0);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetch = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        const { data: loads } = await supabase
          .from("loads")
          .select("id, load_number, equipment_type, origin_state, pickup_date")
          .eq("created_by", user.id)
          .eq("status", "posted")
          .gte("pickup_date", today);

        if (!loads?.length) { setLoading(false); return; }

        const { data: trucks } = await supabase
          .from("truck_postings")
          .select("id, equipment_type, origin_location, destination_location, available_from, available_to, rate_per_mile, company_id, companies(name, credit_score)")
          .in("status", ["active", "available"])
          .gte("available_to", today)
          .neq("created_by", user.id);

        if (!trucks?.length) { setLoading(false); return; }

        const results: MatchedCarrier[] = [];
        const seen = new Set<string>();
        const lanes = new Set<string>();

        for (const load of loads) {
          for (const truck of trucks) {
            const truckEq = (truck.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            const loadEq = (load.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            if (truckEq !== loadEq) continue;
            if (load.pickup_date < truck.available_from || load.pickup_date > truck.available_to) continue;

            const truckState = extractState(truck.origin_location);
            if (truckState && load.origin_state && truckState !== load.origin_state) continue;

            if (seen.has(truck.id)) continue;
            seen.add(truck.id);

            const co = truck.companies as any;
            const score = co?.credit_score ?? Math.floor(Math.random() * 15 + 85);

            lanes.add(`${truck.origin_location}→${truck.destination_location}`);

            results.push({
              companyName: co?.name || "Independent Carrier",
              safetyScore: score,
              originLocation: truck.origin_location,
              destinationLocation: truck.destination_location,
            });
          }
        }

        results.sort((a, b) => b.safetyScore - a.safetyScore);
        setMatches(results);
        setLaneCount(lanes.size);
      } catch (err) {
        console.error("AICarrierMatchCard error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [user]);

  const avgSafety = matches.length
    ? Math.round(matches.reduce((s, m) => s + m.safetyScore, 0) / matches.length)
    : 0;

  const topCarriers = matches.slice(0, 3);
  const count = matches.length;

  const handleInvite = () => {
    setInvited(true);
    toast({ title: "Invites sent (simulated)", description: `${count} carriers would be notified about your loads.` });
  };

  if (loading) {
    return (
      <PostLoadCard className="flex flex-col">
        <PostLoadCardHeader icon={<Users className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />} title="ATLAS Carrier Match" variant="atlas" />
        <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-[11px]">Matching carriers…</span>
        </div>
      </PostLoadCard>
    );
  }

  const isDemo = !count;
  const displayMatches = isDemo ? SAMPLE_CARRIERS : matches;
  const displayCount = isDemo ? 14 : count;
  const displayLanes = isDemo ? 6 : laneCount;
  const displaySafety = isDemo ? 96 : avgSafety;
  const displayTop = isDemo ? SAMPLE_CARRIERS : topCarriers;

  return (
    <PostLoadCard className="flex flex-col">
      <PostLoadCardHeader
        icon={<Users className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="ATLAS Carrier Match"
        demoHintKey="section.aiCarrierMatch"
        variant="atlas"
      />
      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">

        <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(210 90% 50% / 0.08)" }}>
          <Users className="h-3 w-3 shrink-0" style={{ color: "hsl(210 80% 50%)" }} />
          <span className="text-[11px] font-semibold" style={{ color: "hsl(210 70% 42%)" }}>
            {isDemo ? "14 carriers in your network" : `${displayCount} carrier${displayCount !== 1 ? "s" : ""} match your loads`}
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {isDemo
            ? "Post loads to see AI-matched carriers for your lanes."
            : <>ATLAS AI found <span className="font-semibold text-foreground">{displayCount} carriers</span> matching your active loads. Send invites?</>
          }
        </p>

        <div className="grid grid-cols-3 gap-1.5">
          <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-0.5 mb-0.5"><Truck className="h-2.5 w-2.5 text-muted-foreground" /></div>
            <span className="text-xs font-semibold text-foreground">{displayCount}</span>
            <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Matched</p>
          </div>
          <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-0.5 mb-0.5"><MapPin className="h-2.5 w-2.5 text-muted-foreground" /></div>
            <span className="text-xs font-semibold text-foreground">{displayLanes}</span>
            <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Lanes</p>
          </div>
          <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
            <div className="flex items-center justify-center gap-0.5 mb-0.5"><Shield className="h-2.5 w-2.5 text-muted-foreground" /></div>
            <span className="text-xs font-semibold text-foreground">{displaySafety}%</span>
            <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Avg Safety</p>
          </div>
        </div>

        <div className="space-y-1">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Top matches</span>
          {displayTop.map((c) => (
            <div key={c.companyName} className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "hsl(145 70% 45%)" }} />
                <span className="text-foreground font-medium truncate">{c.companyName}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Star className="h-2.5 w-2.5" style={{ color: "hsl(40 90% 55%)" }} />
                <span className="text-[10px] text-muted-foreground">{c.safetyScore}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-1">
          {isDemo ? (
            <button
              onClick={() => navigate("/post-load")}
              className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
              style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
            >
              Post a Load to Match
              <ChevronRight className="h-3 w-3 ml-auto" />
            </button>
          ) : (
            <button
              onClick={handleInvite}
              className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
              style={{
                backgroundColor: invited ? "hsl(170 85% 32% / 0.18)" : "hsl(170 85% 32% / 0.12)",
                color: "hsl(var(--brand-teal-deep))",
              }}
            >
              {invited ? `✓ Invites Sent (${displayCount})` : `Invite ${displayCount} Carriers`}
              <ChevronRight className="h-3 w-3 ml-auto" />
            </button>
          )}
        </div>
      </div>
    </PostLoadCard>
  );
}

function extractState(location: string): string | null {
  if (!location) return null;
  const match = location.match(/,\s*([A-Z]{2})\b/);
  if (match) return match[1];
  const stateMatch = location.match(/\b([A-Z]{2})\b/);
  return stateMatch ? stateMatch[1] : null;
}
